// ===============================================================
// ✅ OnlineUsers.js — Online users (FINAL LIVORA VERSION + VPN PLAN B)
// - VPN PLAN (B): show Privacy/Display country instead of real profile country
// - Filter uses displayCountry key
// - LIVORA PLAN: no dir logic here (html dir only)
// ===============================================================

import React, { useEffect, useState, useRef, useMemo } from "react";
import "./OnlineUsers.css";
import {
  canCallUser as canCallUserPolicy,
  isUserBusy,
  resolveCallTapAction,
  resolveDisplayName,
} from "../utils/callAvailability";

import { db, auth } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
  setDoc,
} from "firebase/firestore";

import { useNavigate, useLocation } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useTranslation } from "react-i18next";
import { defaultAvatarByGender } from "../utils/defaultAvatar";
import { resolveProfilePhotoUrl } from "../utils/profilePhotoResolver";
import { CountryFlagGlyph } from "../utils/countryFlags";
import { useCallStartOverlay } from "../calls/useCallStartOverlay";
import { getStartCallErrorToast } from "../calls/startCallErrors";
import {
  startCallStartupTraceAt,
  logCallStartupEvent,
} from "../calls/callStartupTrace";
import {
  buildReturnContextFromLocation,
  saveReturnContext,
} from "../calls/nativeCallReturnContext";
import useLivoraToast from "../hooks/useLivoraToast";
import LivoraToast from "./common/LivoraToast";

// ✅ VPN PLAN: get privacy/display country for listing + filtering
function getDisplayCountryCode(u) {
  // ✅ SERVER SOURCE OF TRUTH:
  // displayCountry (set by server) -> countryKey (set/restored by server)
  const code = (u?.displayCountry || u?.countryKey || "")
    .toString()
    .trim()
    .toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : "";
}

function profileReceiveMode(u) {
  const r = String(
    u?.receiveMode ?? u?.settings?.messages?.receiveMode ?? "both"
  )
    .trim()
    .toLowerCase();
  if (r === "messages" || r === "off") return r;
  return "both";
}

function debugOnlineUsersList(users, label, extra = {}) {
  console.group(`ONLINE USERS DEBUG — ${label}`);
  if (Object.keys(extra).length) console.log("context:", extra);
  users.forEach((u) => {
    console.log({
      uid: u.id || u.uid,
      nickname: u.nickname || u.name || u.displayName || u.username,
      online: u.online,
      inApp: u.inApp,
      accountStatus: u.accountStatus,
      gender: u.gender,
      receiveMode: u?.receiveMode ?? u?.settings?.messages?.receiveMode,
      verificationStatus: u.verificationStatus,
      hidden: u.hidden,
      isHidden: u.isHidden,
      showInHome: u.showInHome,
      hasPhoto: !!(u.photoURL || u.profileImage),
    });
  });
  console.groupEnd();
}

function OnlineUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]); // now contains ISO2 codes counts
  const [selectedCountry, setSelectedCountry] = useState("all"); // "all" or ISO2 code
  const [showCountryList, setShowCountryList] = useState(false);
  const [loading, setLoading] = useState(true);

  // 🔁 Follow state (Realtime)
  const [followingMap, setFollowingMap] = useState({});
  const [avatarMap, setAvatarMap] = useState({});

  const { begin: beginCallStart, logReturned, logFailed, starting, overlay } =
    useCallStartOverlay();
  const { toast, showToast } = useLivoraToast();

  // ===============================================================
  // 🔥 Realtime online users
  // ===============================================================
  useEffect(() => {
    const q = query(
      collection(db, "publicProfiles"),
      where("online", "==", true),
      orderBy("lastSeenAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        console.log("[OnlineUsers] Firestore query", {
          collection: "publicProfiles",
          where: [{ field: "online", op: "==", value: true }],
          orderBy: { field: "lastSeenAt", direction: "desc" },
          limit: null,
          snapshotCount: list.length,
        });

        debugOnlineUsersList(list, "OnlineUsers after Firestore");

        setUsers(list);

        // ✅ VPN PLAN: Group by displayCountry (ISO2)
        const map = {};
        list.forEach((u) => {
          const code = getDisplayCountryCode(u);
          if (code) map[code] = (map[code] || 0) + 1;
        });

        setCountries(
          Object.entries(map)
            .map(([code, count]) => ({ code, count }))
            .sort((a, b) => b.count - a.count)
        );

        setLoading(false);
      },
      () => setLoading(false)
    );

    return () => unsub();
  }, []);

  // ===============================================================
  // 🔁 Realtime following (Follow / Unfollow)
  // ===============================================================
  useEffect(() => {
    const me = auth.currentUser;
    if (!me) return;

    const q = query(collection(db, "follows"), where("fromUid", "==", me.uid));

    const unsub = onSnapshot(q, (snap) => {
      const map = {};
      snap.forEach((d) => {
        map[d.data().toUid] = d.id; // store follow docId
      });
      setFollowingMap(map);
    });

    return () => unsub();
  }, []);

  // ===============================================================
  // 🧹 Close dropdown on outside click
  // ===============================================================
  useEffect(() => {
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCountryList(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // ===============================================================
  // 📞 Start call
  // ===============================================================
  const handleStartCall = async (targetUser) => {
    const me = auth.currentUser;
    if (!me) {
      alert(t("login_required", { defaultValue: "Please login first" }));
      return;
    }

    if (me.uid === targetUser.id) return;

    if (profileReceiveMode(targetUser) !== "both") {
      return;
    }

    if (!beginCallStart()) return;

    const callButtonTapAt = performance.now();

    try {
      const fn = getFunctions(undefined, "us-central1");

      const isBlocked = httpsCallable(fn, "isBlocked");
      const res = await isBlocked({ otherUid: targetUser.id });
      if (res.data?.blocked) {
        logFailed("blocked", { otherUid: targetUser.id });
        showToast(
          "error",
          t("call_blocked", { defaultValue: "Call is blocked" })
        );
        return;
      }

      const startCall = httpsCallable(fn, "startCall");
      const call = await startCall({ femaleUid: targetUser.id });

      if (!call.data?.callId) {
        logFailed("no_call_id", {
          femaleUid: targetUser.id,
          data: call.data,
        });
        alert(t("call_failed", { defaultValue: "Call failed" }));
        return;
      }

      startCallStartupTraceAt(call.data.callId, callButtonTapAt);
      logCallStartupEvent(call.data.callId, "call_button_tapped", "caller");
      logCallStartupEvent(call.data.callId, "call_created", "caller");

      logReturned(call.data.callId, { femaleUid: targetUser.id });
      const returnContext = buildReturnContextFromLocation(location);
      const uid = auth.currentUser?.uid;
      if (uid && returnContext) {
        saveReturnContext(uid, call.data.callId, returnContext);
        console.log(
          `[callReturn] captured role=caller callId=${call.data.callId} url=${returnContext.url}`
        );
      }
      navigate(`/call-outgoing/${call.data.callId}`, {
        state: {
          returnTo: returnContext?.url || "/online",
          returnContext: returnContext || undefined,
        },
      });
    } catch (e) {
      logFailed(e, { femaleUid: targetUser.id });
      const targetName = resolveDisplayName(targetUser, t);
      const { type, message } = getStartCallErrorToast(t, e, targetName);
      showToast(type, message);
    }
  };

  // ===============================================================
  // ➕ Follow / Unfollow (Realtime + Rules-safe)
  // ===============================================================
  const handleToggleFollow = async (targetUid) => {
    try {
      const me = auth.currentUser;
      if (!me) return;
      if (me.uid === targetUid) return;

      const followId = `${me.uid}_${targetUid}`; // 🔑 ID ثابت
      const ref = doc(db, "follows", followId);

      if (followingMap[targetUid]) {
        // ❌ إلغاء متابعة
        await deleteDoc(ref);
      } else {
        // ➕ إضافة متابعة
        await setDoc(
          ref,
          {
            fromUid: me.uid,
            toUid: targetUid,
            createdAt: Date.now(), // int ✔
          },
          { merge: false } // 🚨 مهم جدًا
        );
      }
    } catch (e) {
      console.error("toggleFollow error:", e);
    }
  };

  // ===============================================================
  // 🎯 Filter users (by displayCountry)
  // ===============================================================
  const filteredUsers = useMemo(() => {
    const out =
      selectedCountry === "all"
        ? users
        : users.filter((u) => {
            const code = getDisplayCountryCode(u);
            if (code !== selectedCountry) {
              console.log("FILTER OUT: country", u.id, {
                expected: selectedCountry,
                actual: code,
              });
              return false;
            }
            return true;
          });
    debugOnlineUsersList(out, "OnlineUsers filteredUsers (final list)", {
      selectedCountry,
    });
    return out;
  }, [users, selectedCountry]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!filteredUsers.length) {
        if (alive) setAvatarMap({});
        return;
      }

      const entries = await Promise.all(
        filteredUsers.map(async (u) => {
          const url = await resolveProfilePhotoUrl(u?.profileImage || "", u?.gender);
          return [u.id, url];
        })
      );

      if (!alive) return;

      const map = {};
      for (const [uid, url] of entries) {
        if (uid) map[uid] = url;
      }
      setAvatarMap(map);
    };

    run();

    return () => {
      alive = false;
    };
  }, [filteredUsers]);

  // ===============================================================
  // For rendering: map user -> display country label + flag
  // ===============================================================
  const getCountryLabel = useMemo(() => {
    return (code) =>
      t(`countries.${(code || "").toLowerCase()}`, { defaultValue: code || "—" });
  }, [t]);

  if (loading) {
    return <div className="online-loading">{t("loading", { defaultValue: "Loading..." })}</div>;
  }

  // ===============================================================
  // 🎨 UI
  // ===============================================================
  return (
    <div className="online-page">
      {/* ===== Floating Back Button ===== */}
      <button className="top-icon-btn back" onClick={() => navigate("/home")} type="button">
        ←
      </button>

      {/* ===== Header ===== */}
      <header className="online-header">
        <h2 className="online-title">
          {t("online_users", { defaultValue: "Online Users" })}
        </h2>

        <span className="online-count">
          {t("online_count", { defaultValue: "{{count}} online", count: filteredUsers.length })}
        </span>

        <button
          className="filter-btn"
          onClick={() => setShowCountryList((v) => !v)}
          type="button"
          aria-label={t("filter", { defaultValue: "Filter" })}
        >
          🌍
        </button>
      </header>

      {/* ===== Country filter ===== */}
      {showCountryList && (
        <div className="country-dropdown" ref={dropdownRef}>
          <div
            className={`country-item ${selectedCountry === "all" ? "active" : ""}`}
            onClick={() => setSelectedCountry("all")}
            role="button"
            tabIndex={0}
          >
            🌍 {t("all_countries", { defaultValue: "All countries" })} ({users.length})
          </div>

{countries
  .filter((c) => c?.code && /^[A-Z]{2}$/.test(c.code))
  .map((c) => {
    const label = getCountryLabel(c.code);

    return (
      <div
        key={c.code}
        className={`country-item ${selectedCountry === c.code ? "active" : ""}`}
        onClick={() => setSelectedCountry(c.code)}
        role="button"
        tabIndex={0}
        title={t("countries.code_title", {
          defaultValue: "Country code: {{code}}",
          code: c.code,
        })}
      >
        <CountryFlagGlyph countryCode={c.code} /> {label} ({c.count})
      </div>
    );
  })}

        </div>
      )}

      {/* ===== Users grid ===== */}
      <div className="users-grid">
        {filteredUsers.map((user) => {
          const code = getDisplayCountryCode(user);
const label = code
  ? getCountryLabel(code)
  : (
      user.countryName ||
      user.country ||
      (user.countryKey ? user.countryKey.toUpperCase() : "") ||
      t("common.unknown", { defaultValue: "—" })
    );

          const receiveMode = profileReceiveMode(user);
          // Phase N9B: inCall is the server-authoritative Busy mirror (see
          // functions/calls/locks.js).
          const isBusy = isUserBusy(user);
          const canCallUser = canCallUserPolicy(user);

          return (
            <div key={user.id} className="user-card">
              {/* Follow button */}
              <button
                className={`follow-btn ${followingMap[user.id] ? "following" : ""}`}
                onClick={() => handleToggleFollow(user.id)}
                type="button"
                aria-label={t("follow", { defaultValue: "Follow" })}
              >
                {followingMap[user.id] ? "✓" : "+"}
              </button>

              <div className="avatar-wrap">
                <img
                  src={
                    avatarMap[user.id] ||
                    defaultAvatarByGender(user.gender)
                  }
                  alt={t("common.avatar", { defaultValue: "Avatar" })}
                  className="avatar"
                  onError={(e) => {
                    e.currentTarget.src = defaultAvatarByGender(user.gender);
                  }}
                />
                <span
                  className={`dot ${isBusy ? "dot-busy" : "dot-online"}`}
                  role="img"
                  aria-label={
                    isBusy
                      ? t("presence.busy_now", { defaultValue: "Busy now" })
                      : undefined
                  }
                />
              </div>

              <div className="user-meta">
                <div className="user-name">{user.name ||
                    user.displayName ||
                    user.username ||
                    t("user", { defaultValue: "User" })}</div>

                {/* ✅ VPN PLAN (B): show display country */}
                <div className="user-sub">
                  {code ? (
                    <CountryFlagGlyph countryCode={code} />
                  ) : user.flagEmoji ? (
                    <span aria-hidden>{user.flagEmoji}</span>
                  ) : (
                    <CountryFlagGlyph />
                  )}{" "}
                  {label}
                </div>

                {isBusy && (
                  <span className="user-busy-label">
                    {t("presence.busy_now", { defaultValue: "Busy now" })}
                  </span>
                )}
              </div>

              <div className="card-actions">
                <button
                  className={`call-btn${canCallUser ? "" : " call-btn--off"}`}
                  onClick={() => {
                    if (starting) return;
                    const tap = resolveCallTapAction(user, t);
                    if (tap.action === "busy") {
                      showToast("error", tap.message);
                      return;
                    }
                    if (tap.action !== "call") return;
                    handleStartCall(user);
                  }}
                  type="button"
                  disabled={(receiveMode !== "both" && !isBusy) || starting}
                  aria-disabled={!canCallUser || starting}
                  aria-busy={starting}
                  title={
                    !canCallUser
                      ? isBusy || receiveMode === "off"
                        ? t("calls.user_unavailable", {
                            defaultValue: "User unavailable",
                          })
                        : t("calls_not_available", {
                            defaultValue: "Calls not available",
                          })
                      : t("start_call", { defaultValue: "Start call" })
                  }
                >
                  📹
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {overlay}
      <LivoraToast toast={toast} />
    </div>
  );
}

export default OnlineUsers;
